import React, { useState, useEffect, useCallback } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { PageHeader, RevealPasswordModal } from '../components/common';
import { ROUTES } from '../constants';
import { merchantService, marketerService, ApiError } from '../services';

const MerchantDetailPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { id } = useParams();
  const merchant = location.state?.merchant;
  const backToUpline = location.state?.backToUpline || null;
  const breadcrumbsOverride = location.state?.breadcrumbsOverride || null;
  const [detailMerchant, setDetailMerchant] = useState(merchant || null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [downline, setDownline] = useState([]);
  const [downlineLoading, setDownlineLoading] = useState(false);
  const [downlineError, setDownlineError] = useState(null);
  const [downlinePagination, setDownlinePagination] = useState({
    page: 1,
    pageCount: 1,
    size: 20,
    count: 0,
    total: 0,
  });

  // Accordion state for each card
  const [openDetails, setOpenDetails] = useState(true);
  const [openOwner, setOpenOwner] = useState(true);
  const [openAdditional, setOpenAdditional] = useState(true);

  // BE masks sensitive fields by default; admin opt-in via reveal-verify
  // (POST /api/v1/account/admin/reveal-verify) elevates session for 5 min.
  const [revealModalOpen, setRevealModalOpen] = useState(false);
  // `revealed` is only authoritative AFTER fresh detail fetch; list endpoint
  // doesn't include it, so gate the toggle button on detailFetched to prevent
  // a Show-button flash on refresh.
  const [detailFetched, setDetailFetched] = useState(false);
  // Local visual hide on top of BE-raw data while elevation is still active.
  // Toggling does NOT call the API; BE stays revealed until TTL ends.
  const [localHidden, setLocalHidden] = useState(false);

  const revealed = detailMerchant?.revealed === true;
  const effectivelyMasked = !revealed || localHidden;

  // Account status and app visibility are two different things. `status === 'A'`
  // only means the account is not suspended/deleted; the SKAi1 app additionally
  // hides a merchant whose marketing balance went negative (can no longer fund
  // cashback) or whose subscription lapsed past the grace period. Labelling
  // such a merchant "Active" here misleads ops, so surface it as "Offline"
  // with the reason(s) on hover.
  //
  // `subscriptionActive` is only sent by the detail endpoint, so it stays null
  // while the page still renders list-sourced data — compared strictly against
  // false to avoid flashing "Offline" before the detail fetch lands. Merchants
  // without a margin are hidden from the app too, but BE does not surface that
  // here, so this stays a partial mirror of the app-side filter.
  const hiddenReasons = [];
  if (detailMerchant?.hasNegativeBalance === true) {
    hiddenReasons.push('marketing balance is negative');
  }
  if (detailMerchant?.subscriptionActive === false) {
    hiddenReasons.push('subscription is inactive');
  }
  const hiddenFromApp =
    detailMerchant?.isActive === true && hiddenReasons.length > 0;
  const statusLabel = hiddenFromApp
    ? 'Offline'
    : detailMerchant?.isActive
    ? 'Active'
    : 'Inactive';
  const statusClassName = hiddenFromApp
    ? 'bg-amber-100 text-amber-700'
    : detailMerchant?.isActive
    ? 'bg-green-100 text-green-700'
    : 'bg-gray-100 text-gray-700';
  const statusTitle = hiddenFromApp
    ? `Hidden from the SKAi1 app: ${hiddenReasons.join('; ')}`
    : undefined;

  const maskTail = (value, keep = 4) => {
    if (!value) return '-';
    const str = String(value);
    if (str.length <= keep) return '*'.repeat(Math.max(2, str.length));
    return `**${str.slice(-keep)}`;
  };
  const maskFull = value => (value ? '******' : '-');
  // Pre-fetch we render a blank placeholder so list-state values never leak
  // raw. Zero-width space stays truthy and survives `.trim()` in
  // `formatDetail()`, so visually it renders empty without the '-' fallback.
  const sensitiveValue = (value, masker = maskFull) => {
    if (!detailFetched) return '​';
    return revealed && localHidden ? masker(value) : value;
  };

  const handleToggleReveal = () => {
    if (revealed) {
      setLocalHidden(prev => !prev);
    } else {
      setRevealModalOpen(true);
    }
  };

  const formatCurrencyEUR = value => {
    const numericValue =
      value === null || value === undefined || value === '' ? 0 : Number(value);

    return new Intl.NumberFormat('en-IE', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(Number.isFinite(numericValue) ? numericValue : 0);
  };

  const formatPercent = value => {
    if (value === null || value === undefined || value === '') return '-';
    const numericValue = Number(value);
    if (!Number.isFinite(numericValue)) return '-';
    return `${numericValue.toFixed(2).replace(/\.00$/, '')}%`;
  };

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
  }, [id]);

  // Keep local merchant details in sync with route changes
  useEffect(() => {
    setDetailMerchant(merchant || null);
    setDetailFetched(false);
    setLocalHidden(false);
  }, [id, merchant]);

  const fetchMerchantDetails = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    setError(null);
    try {
      const response = await merchantService.getFormattedMerchantDetails(id);
      if (response && response.data) {
        setDetailMerchant(response.data);
        setDetailFetched(true);
      }
    } catch (err) {
      console.error('Failed to fetch merchant details:', err);
      if (err instanceof ApiError) {
        setError(`Failed to load merchant details: ${err.message}`);
      } else {
        setError('Failed to load merchant details. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchMerchantDetails();
  }, [fetchMerchantDetails]);

  const fetchMerchantDownline = useCallback(
    async (page = 1) => {
      if (!detailMerchant?.accountId) return;

      setDownlineLoading(true);
      setDownlineError(null);

      try {
        const response = await marketerService.getFormattedShopperDownline(
          detailMerchant.accountId,
          {
            page,
            size: downlinePagination.size,
            order: 'created_at DESC',
            from: 'P',
          }
        );

        if (response && response.data) {
          setDownline(response.data.data || []);
          setDownlinePagination({
            page: response.data.page || 1,
            pageCount: response.data.pageCount || 1,
            size: response.data.size || 20,
            count: response.data.count || 0,
            total: response.data.total || 0,
          });
        }
      } catch (err) {
        console.error('Failed to fetch merchant downline:', err);
        setDownline([]);
        if (err instanceof ApiError) {
          setDownlineError(`Failed to load downline: ${err.message}`);
        } else {
          setDownlineError('Failed to load downline. Please try again.');
        }
      } finally {
        setDownlineLoading(false);
      }
    },
    [detailMerchant?.accountId, downlinePagination.size]
  );

  useEffect(() => {
    if (!detailMerchant?.accountId) return;
    fetchMerchantDownline(1);
  }, [detailMerchant?.accountId, fetchMerchantDownline]);

  const handleDownlinePageChange = useCallback(
    page => {
      fetchMerchantDownline(page);
    },
    [fetchMerchantDownline]
  );

  // Redirect to merchants list if no merchant data is provided and fetch failed
  useEffect(() => {
    if (!loading && !detailMerchant && error) {
      navigate(ROUTES.MERCHANTS, { replace: true });
    }
  }, [detailMerchant, navigate, loading, error]);

  if (loading && !detailMerchant) {
    return (
      <div className='flex items-center justify-center min-h-screen'>
        <div className='flex items-center space-x-2'>
          <svg
            className='animate-spin h-8 w-8 text-blue-600'
            fill='none'
            viewBox='0 0 24 24'
          >
            <circle
              className='opacity-25'
              cx='12'
              cy='12'
              r='10'
              stroke='currentColor'
              strokeWidth='4'
            ></circle>
            <path
              className='opacity-75'
              fill='currentColor'
              d='M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z'
            ></path>
          </svg>
          <span className='text-gray-600'>Loading merchant details...</span>
        </div>
      </div>
    );
  }

  if (!detailMerchant) {
    return null; // Will redirect
  }

  const formatDetail = value => {
    if (value == null) return '-';
    const s = String(value).trim();
    if (s === '' || s === 'N/A') return '-';
    return s;
  };

  const formatCoord = (lat, lon) => {
    const hasLat = lat !== null && lat !== undefined && lat !== '';
    const hasLon = lon !== null && lon !== undefined && lon !== '';
    if (!hasLat || !hasLon) return '-';
    if ((lat === 0 || lat === '0') && (lon === 0 || lon === '0')) return '-';
    return `${lat}, ${lon}`;
  };

  // TODO(BE-wiring): re-enable when audit data is available
  // const lastModifiedText = (() => {
  //   const date = formatDetail(detailMerchant.lastModified);
  //   const by = detailMerchant.modifiedByName || null;
  //   return by ? `${date} (${by})` : date;
  // })();

  const goEditBilling = () =>
    navigate(
      ROUTES.generatePath(ROUTES.MERCHANTS_EDIT_BILLING, {
        id: detailMerchant.id,
      }),
      { state: { merchant: detailMerchant } }
    );

  const goEditBusinessOwner = () =>
    navigate(
      ROUTES.generatePath(ROUTES.MERCHANTS_EDIT_BUSINESS_OWNER, {
        id: detailMerchant.id,
      }),
      { state: { merchant: detailMerchant } }
    );

  const goEditBusinessDetails = () =>
    navigate(
      ROUTES.generatePath(ROUTES.MERCHANTS_EDIT_BUSINESS_DETAILS, {
        id: detailMerchant.id,
      }),
      { state: { merchant: detailMerchant } }
    );

  const handleDownlineDetail = member => {
    if (!member?.id) return;

    const uplineRoute = ROUTES.generatePath(ROUTES.MERCHANTS_DETAIL, { id });
    const currentBreadcrumbs =
      breadcrumbsOverride ||
      [
        { name: 'Home', href: ROUTES.DASHBOARD },
        { name: 'Merchants', href: ROUTES.MERCHANTS },
        {
          name: detailMerchant?.companyName || 'Detail',
          href: uplineRoute,
        },
      ];
    // Stack behavior: back should return to immediate previous page
    const nextBackToUpline = {
      label: 'Back to Upline',
      to: uplineRoute,
      state: {
        merchant: detailMerchant,
        breadcrumbsOverride: currentBreadcrumbs,
        backToUpline: backToUpline || null,
      },
    };

    const shopperLabel =
      member.fullName ||
      [member.firstName, member.lastName].filter(Boolean).join(' ') ||
      member.id;
    const targetCrumbs = [
      ...currentBreadcrumbs,
      {
        name: shopperLabel,
        href: ROUTES.generatePath(ROUTES.SHOPPERS_DETAIL, { id: member.id }),
      },
    ];
    navigate(ROUTES.generatePath(ROUTES.SHOPPERS_DETAIL, { id: member.id }), {
      state: {
        backToUpline: nextBackToUpline,
        breadcrumbsOverride: targetCrumbs,
        shopper: {
          ...member,
          referralCode: member.referralCode || member.referral,
        },
      },
    });
  };

  const downlineDisplayStart =
    downlinePagination.count > 0
      ? (downlinePagination.page - 1) * downlinePagination.size + 1
      : 0;
  const downlineDisplayEnd =
    downlinePagination.count > 0
      ? Math.min(
          downlinePagination.page * downlinePagination.size,
          downlinePagination.count
        )
      : 0;
  const downlinePageNumbers = Array.from(
    new Set(
      [
        1,
        downlinePagination.page - 1,
        downlinePagination.page,
        downlinePagination.page + 1,
        downlinePagination.pageCount,
      ].filter(
        page => page >= 1 && page <= downlinePagination.pageCount
      )
    )
  );

  // Reusable UI primitives
  const Field = ({ label, value, mono = false, className = '' }) => (
    <div className={className}>
      <p className='text-xs text-gray-500 mb-1'>{label}</p>
      <p
        className={`text-sm text-gray-900 break-words ${
          mono ? 'font-mono' : ''
        }`}
      >
        {value}
      </p>
    </div>
  );

  const PencilIcon = ({ className = 'w-4 h-4' }) => (
    <svg
      className={className}
      fill='none'
      stroke='currentColor'
      strokeWidth={2}
      viewBox='0 0 24 24'
    >
      <path
        strokeLinecap='round'
        strokeLinejoin='round'
        d='M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z'
      />
    </svg>
  );

  const ChevronIcon = ({ open }) => (
    <svg
      className={`w-5 h-5 text-gray-500 transition-transform duration-200 ${
        open ? 'rotate-180' : ''
      }`}
      fill='none'
      stroke='currentColor'
      strokeWidth={2}
      viewBox='0 0 24 24'
    >
      <path strokeLinecap='round' strokeLinejoin='round' d='M19 9l-7 7-7-7' />
    </svg>
  );

  const SectionHeader = ({ title, onEdit, showLastModified = true }) => (
    <div className='mb-5'>
      <div className='flex items-center gap-2'>
        <h3 className='text-base font-semibold text-gray-900'>{title}</h3>
        {onEdit && (
          <button
            type='button'
            onClick={onEdit}
            className='inline-flex text-blue-600 hover:text-blue-800 p-0.5 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1'
            aria-label={`Edit ${title}`}
          >
            <PencilIcon />
          </button>
        )}
      </div>
      {/* TODO(BE-wiring): replace static placeholder with audit data */}
      {/* {showLastModified && (
        <p className='text-xs text-gray-500 mt-0.5'>
          Last Modified{' '}
          <span className='text-gray-700 font-medium'>{lastModifiedText}</span>
        </p>
      )} */}
    </div>
  );

  return (
    <div className='space-y-6'>
      <PageHeader
        title='Merchant Details'
        subtitle='View detailed information about this merchant and their downline'
        breadcrumbs={
          breadcrumbsOverride || [
            { name: 'Home', href: ROUTES.DASHBOARD },
            { name: 'Merchants', href: ROUTES.MERCHANTS },
            { name: detailMerchant.companyName },
          ]
        }
      />

      {/* Error Display */}
      {error && (
        <div className='bg-red-50 border border-red-200 rounded-lg p-4'>
          <div className='flex items-center'>
            <svg
              className='w-5 h-5 text-red-400 mr-2'
              fill='none'
              stroke='currentColor'
              viewBox='0 0 24 24'
            >
              <path
                strokeLinecap='round'
                strokeLinejoin='round'
                strokeWidth={2}
                d='M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z'
              />
            </svg>
            <p className='text-sm text-red-700'>{error}</p>
          </div>
        </div>
      )}

      {/* Main Merchant Information Card */}
      <div className='bg-white shadow-sm border border-gray-200 rounded-lg'>
        {/* Card Header */}
        <div className='flex items-start justify-between p-6 gap-4'>
          <div className='min-w-0'>
            <h2 className='text-xl font-bold text-gray-900'>
              {detailMerchant.companyName}
            </h2>
            <p className='text-sm text-gray-500 mt-1 break-all'>
              Merchant ID: {detailMerchant.id}
            </p>
          </div>
          <div className='flex items-center gap-2 pt-1 flex-shrink-0'>
            <span
              className={`inline-flex px-3 py-0.5 text-xs font-medium rounded-full ${statusClassName}`}
              title={statusTitle}
            >
              {statusLabel}
            </span>
            {detailFetched && (
              <button
                type='button'
                onClick={handleToggleReveal}
                className='inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md'
              >
                {effectivelyMasked ? (
                  <svg
                    className='w-4 h-4'
                    fill='none'
                    stroke='currentColor'
                    viewBox='0 0 24 24'
                  >
                    <path
                      strokeLinecap='round'
                      strokeLinejoin='round'
                      strokeWidth={2}
                      d='M15 12a3 3 0 11-6 0 3 3 0 016 0z'
                    />
                    <path
                      strokeLinecap='round'
                      strokeLinejoin='round'
                      strokeWidth={2}
                      d='M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z'
                    />
                  </svg>
                ) : (
                  <svg
                    className='w-4 h-4'
                    fill='none'
                    stroke='currentColor'
                    viewBox='0 0 24 24'
                  >
                    <path
                      strokeLinecap='round'
                      strokeLinejoin='round'
                      strokeWidth={2}
                      d='M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.542-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.542 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21'
                    />
                  </svg>
                )}
                {effectivelyMasked ? 'Show' : 'Hide'}
              </button>
            )}
            <button
              type='button'
              onClick={() => setOpenDetails(v => !v)}
              aria-label={openDetails ? 'Collapse' : 'Expand'}
              className='p-1.5 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-md'
            >
              <ChevronIcon open={openDetails} />
            </button>
          </div>
        </div>

        {openDetails && (
          <div className='px-6 pb-6 space-y-8'>
            {/* Business Details */}
            <section>
              <SectionHeader
                title='Business Details'
                onEdit={goEditBusinessDetails}
              />
              <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-5'>
                <Field
                  label='Company Name'
                  value={formatDetail(detailMerchant.companyName)}
                />
                <Field
                  label='Email'
                  value={formatDetail(detailMerchant.email)}
                />
                <Field
                  label='Phone Number'
                  value={formatDetail(detailMerchant.phone)}
                />
                <Field
                  label='Sector'
                  value={formatDetail(detailMerchant.sector)}
                />
                <Field
                  label='Address'
                  value={formatDetail(detailMerchant.address)}
                />
                <Field
                  label='Country'
                  value={formatDetail(detailMerchant.country)}
                />
                <Field
                  label='Province'
                  value={formatDetail(detailMerchant.province)}
                />
                <Field label='City' value={formatDetail(detailMerchant.city)} />
                <Field
                  label='ZIP Code'
                  value={formatDetail(detailMerchant.zip)}
                />
                <Field
                  label='Google Place ID'
                  mono
                  value={formatDetail(detailMerchant.googleID)}
                />
                <Field
                  label='Coordinate'
                  value={formatCoord(detailMerchant.lat, detailMerchant.lon)}
                />
                {/* Pending BE / product: field belum di response API (bankDetails) */}
                {/* <Field
                  label='Bank Details'
                  value={formatDetail(detailMerchant.bankDetails)}
                /> */}
              </div>
            </section>

            <div className='border-t border-gray-200' />

            {/* Business Balance */}
            <section>
              <SectionHeader
                title='Business Balance'
                showLastModified={false}
              />
              <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-5'>
                <Field
                  label='Marketing Balance'
                  value={formatCurrencyEUR(detailMerchant.balance)}
                />
                <Field
                  label='Marketing Boost'
                  value={formatCurrencyEUR(detailMerchant.marketingBoost)}
                />
                <Field
                  label='Contributed Volume Points'
                  value={formatDetail(detailMerchant.careerPoint)}
                />
              </div>
            </section>

            <div className='border-t border-gray-200' />

            {/* Billing Details */}
            <section>
              <SectionHeader title='Billing Details' onEdit={goEditBilling} />
              <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-5'>
                <Field
                  label='Partita Iva'
                  value={formatDetail(
                    sensitiveValue(
                      detailMerchant.partitaIva ??
                        detailMerchant.billingPartitaIva,
                      maskTail
                    )
                  )}
                />
                <Field
                  label='Legal Company Name'
                  value={formatDetail(detailMerchant.billingLegalCompanyName)}
                />
                <Field
                  label='Address'
                  value={formatDetail(detailMerchant.billingAddress)}
                />
                <Field
                  label='Province'
                  value={formatDetail(detailMerchant.billingProvince)}
                />
                <Field
                  label='City'
                  value={formatDetail(detailMerchant.billingTown)}
                />
                <Field
                  label='ZIP Code'
                  value={formatDetail(detailMerchant.billingZipCode)}
                />
                <Field
                  label='SDI Code'
                  value={formatDetail(detailMerchant.billingSdiCode)}
                />
                <Field
                  label='PEC'
                  value={formatDetail(detailMerchant.billingPecEmail)}
                />
                {/* TODO(BE-wiring): replace static placeholder with audit data */}
                <Field
                  label='Last Payment Date Platform Fee'
                  value={formatDetail(detailMerchant.lastPlatformFeePaymentDate)}
                />
                <Field
                  label='Last Payment Date Subscription'
                  value={formatDetail(
                    detailMerchant.lastSubscriptionPaymentDate
                  )}
                />
              </div>
            </section>

            <div className='border-t border-gray-200' />

            {/* Network */}
            <section>
              <SectionHeader title='Network' showLastModified={false} />
              <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-5'>
                <Field
                  label='Recommender'
                  value={formatDetail(detailMerchant.recommenderName)}
                />
                <Field
                  label='Direct Partner'
                  value={formatDetail(detailMerchant.partner)}
                />
                <Field
                  label='Total Downline'
                  value={formatDetail(detailMerchant.downline)}
                />
                <Field
                  label='Communication Consent'
                  value={
                    detailMerchant.communicationConsent === true
                      ? 'Active'
                      : detailMerchant.communicationConsent === false
                      ? 'Inactive'
                      : '-'
                  }
                />
              </div>
            </section>
          </div>
        )}
      </div>

      {/* Business Owner Card */}
      <div className='bg-white shadow-sm border border-gray-200 rounded-lg'>
        <button
          type='button'
          onClick={() => setOpenOwner(v => !v)}
          className='w-full flex items-center justify-between p-6 text-left focus:outline-none'
        >
          <div>
            <div className='flex items-center gap-2'>
              <h3 className='text-base font-semibold text-gray-900'>
                Business Owner
              </h3>
              <span
                role='button'
                tabIndex={0}
                aria-label='Edit Business Owner'
                onClick={e => {
                  e.stopPropagation();
                  goEditBusinessOwner();
                }}
                onKeyDown={e => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    e.stopPropagation();
                    goEditBusinessOwner();
                  }
                }}
                className='inline-flex text-blue-600 hover:text-blue-800 p-0.5 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1 cursor-pointer'
              >
                <PencilIcon />
              </span>
            </div>
            {/* TODO(BE-wiring): replace static placeholder with audit data */}
            {/* <p className='text-xs text-gray-500 mt-0.5'>
              Last Modified{' '}
              <span className='text-gray-700 font-medium'>
                {lastModifiedText}
              </span>
            </p> */}
          </div>
          <ChevronIcon open={openOwner} />
        </button>

        {openOwner && (
          <div className='px-6 pb-6'>
            <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-5'>
              <Field
                label='Full Name'
                value={formatDetail(detailMerchant.ownerFullName)}
              />
              <Field
                label='Email Owner'
                value={formatDetail(sensitiveValue(detailMerchant.ownerEmail))}
              />
              <Field
                label='Phone Number'
                value={formatDetail(
                  sensitiveValue(detailMerchant.ownerPhone, maskTail)
                )}
              />
              <Field
                label='Join Date'
                value={formatDetail(detailMerchant.ownerJoinDate)}
              />
            </div>
          </div>
        )}
      </div>

      {/* Additional Information Card */}
      <div className='bg-white shadow-sm border border-gray-200 rounded-lg'>
        <button
          type='button'
          onClick={() => setOpenAdditional(v => !v)}
          className='w-full flex items-center justify-between p-6 text-left focus:outline-none'
        >
          <div>
            <div className='flex items-center gap-2'>
              <h3 className='text-base font-semibold text-gray-900'>
                Additional Information
              </h3>
              {/* TODO(BE-wiring): wire to edit handler when route is ready */}
              {/* <span
                aria-label='Edit Additional Information'
                className='inline-flex text-blue-600 hover:text-blue-800 p-0.5 rounded'
              >
                <PencilIcon />
              </span> */}
            </div>
            {/* TODO(BE-wiring): replace static placeholder with audit data */}
            {/* <p className='text-xs text-gray-500 mt-0.5'>
              Last Modified{' '}
              <span className='text-gray-700 font-medium'>
                {lastModifiedText}
              </span>
            </p> */}
          </div>
          <ChevronIcon open={openAdditional} />
        </button>

        {openAdditional && (
          <div className='px-6 pb-6'>
            <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-5'>
              <Field
                label='Referral Code'
                mono
                value={formatDetail(detailMerchant.referralCode)}
              />
              <Field
                label='Marketing Fee'
                value={formatPercent(detailMerchant.marketingFeePercent)}
              />
              <Field
                label='Voucher'
                mono
                value={formatDetail(detailMerchant.voucher)}
              />
            </div>
          </div>
        )}
      </div>

      {/* Downline Section */}
      <div className='bg-white rounded-lg shadow-sm border border-gray-200'>
        <div className='px-4 md:px-6 py-4 border-b border-gray-200'>
          <h3 className='text-lg font-semibold text-gray-900'>Downline</h3>

          <div className='mt-4 border-b border-gray-200'>
            <nav
              className='flex -mb-px space-x-6'
              role='tablist'
              aria-label='Downline tabs'
            >
              <button
                type='button'
                className='border-blue-600 text-blue-600 whitespace-nowrap border-b-2 px-1 py-2 text-sm font-medium'
              >
                Shopper
              </button>
            </nav>
          </div>

          {downlineError && (
            <div className='mt-3 rounded-md border border-red-200 bg-red-50 px-3 py-2'>
              <p className='text-sm text-red-700'>{downlineError}</p>
            </div>
          )}

          <p className='text-sm text-gray-500 mt-3'>
            Showing {downlineDisplayStart} to {downlineDisplayEnd} of{' '}
            {downlinePagination.count} downline members
          </p>
        </div>

        <div className='overflow-x-auto'>
          <table className='min-w-full divide-y divide-gray-200'>
            <thead className='bg-gray-50'>
              <tr>
                <th className='hidden lg:table-cell px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-56'>
                  ID
                </th>
                <th className='px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider'>
                  First Name
                </th>
                <th className='px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider'>
                  Last Name
                </th>
                <th className='px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider'>
                  Status
                </th>
                <th className='px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider'>
                  Join Date
                </th>
                <th className='px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider'>
                  Referral Code
                </th>
                <th className='px-3 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider'>
                  Direct Partner
                </th>
                <th className='px-3 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider'>
                  Downline
                </th>
                <th className='px-3 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider'>
                  Action
                </th>
              </tr>
            </thead>
            <tbody className='bg-white divide-y divide-gray-200'>
              {downlineLoading && downline.length === 0 && (
                <tr>
                  <td colSpan={9} className='px-4 py-8 text-center text-sm text-gray-500'>
                    Loading downline...
                  </td>
                </tr>
              )}

              {!downlineLoading && downline.length === 0 && (
                <tr>
                  <td colSpan={9} className='px-4 py-8 text-center text-sm text-gray-500'>
                    No downline members found.
                  </td>
                </tr>
              )}

              {downline.map(member => {
                const isHighlight = member.status === 'Plus';
                const referralValue = member.referralCode || member.referral || '-';

                return (
                  <tr key={member.id} className='hover:bg-gray-50 align-top'>
                    <td className='hidden lg:table-cell px-3 py-4 text-xs text-gray-900 font-mono align-top'>
                      <div className='break-all whitespace-normal' title={member.id}>
                        {member.id}
                      </div>
                    </td>
                    <td className='px-4 py-4 align-top'>
                      <div className='text-sm font-medium text-gray-900'>
                        {member.firstName}
                      </div>
                    </td>
                    <td className='px-3 py-4 text-sm font-medium text-gray-900 align-top'>
                      {member.lastName}
                    </td>
                    <td className='px-3 py-4 whitespace-nowrap align-top'>
                      <span
                        className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                          isHighlight
                            ? 'bg-blue-100 text-blue-800'
                            : 'bg-gray-100 text-gray-800'
                        }`}
                      >
                        {member.status || '-'}
                      </span>
                    </td>
                    <td className='px-3 py-4 whitespace-nowrap text-sm text-gray-900 align-top'>
                      {member.joinDate || '-'}
                    </td>
                    <td className='px-3 py-4 text-xs text-gray-900 font-mono align-top'>
                      <div
                        className='max-w-[180px] break-words'
                        title={referralValue}
                      >
                        {referralValue}
                      </div>
                    </td>
                    <td className='px-3 py-4 whitespace-nowrap text-sm text-gray-900 text-right font-semibold align-top'>
                      {member.partner ?? 0}
                    </td>
                    <td className='px-3 py-4 whitespace-nowrap text-sm text-gray-900 text-right font-semibold align-top'>
                      {member.downline ?? 0}
                    </td>
                    <td className='px-3 py-4 whitespace-nowrap text-center align-top'>
                      <button
                        type='button'
                        onClick={() => handleDownlineDetail(member)}
                        className='text-blue-600 hover:text-blue-900 text-sm font-medium'
                      >
                        Detail
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        <div className='px-6 py-4 border-t border-gray-200 flex items-center justify-between'>
          <p className='text-sm text-gray-700'>
            Page <span className='font-medium'>{downlinePagination.page}</span>{' '}
            of{' '}
            <span className='font-medium'>
              {downlinePagination.pageCount}
            </span>
          </p>
          <nav
            className='relative z-0 inline-flex rounded-md shadow-sm -space-x-px'
            aria-label='Pagination'
          >
            <button
              type='button'
              onClick={() =>
                handleDownlinePageChange(downlinePagination.page - 1)
              }
              disabled={downlineLoading || downlinePagination.page <= 1}
              className='relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50'
            >
              <svg className='h-5 w-5' fill='currentColor' viewBox='0 0 20 20'>
                <path
                  fillRule='evenodd'
                  d='M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z'
                  clipRule='evenodd'
                />
              </svg>
            </button>
            {downlinePageNumbers.map(num => (
              <button
                key={num}
                type='button'
                onClick={() => handleDownlinePageChange(num)}
                disabled={downlineLoading}
                className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                  num === downlinePagination.page
                    ? 'z-10 bg-blue-50 border-blue-500 text-blue-600'
                    : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                } disabled:opacity-50`}
              >
                {num}
              </button>
            ))}
            <button
              type='button'
              onClick={() =>
                handleDownlinePageChange(downlinePagination.page + 1)
              }
              disabled={
                downlineLoading ||
                downlinePagination.page >= downlinePagination.pageCount
              }
              className='relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50'
            >
              <svg className='h-5 w-5' fill='currentColor' viewBox='0 0 20 20'>
                <path
                  fillRule='evenodd'
                  d='M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z'
                  clipRule='evenodd'
                />
              </svg>
            </button>
          </nav>
        </div>

        {backToUpline?.to && (
          <div className='px-6 py-4 border-t border-gray-200 flex items-center justify-end'>
            <button
              type='button'
              onClick={() =>
                navigate(backToUpline.to, { state: backToUpline.state })
              }
              className='inline-flex items-center gap-2 px-4 py-2 rounded-md border border-gray-300 bg-white text-sm font-medium text-gray-700 hover:bg-gray-50'
            >
              <svg
                className='w-4 h-4'
                fill='none'
                stroke='currentColor'
                viewBox='0 0 24 24'
              >
                <path
                  strokeLinecap='round'
                  strokeLinejoin='round'
                  strokeWidth={2}
                  d='M15 19l-7-7 7-7'
                />
              </svg>
              {backToUpline.label || 'Back to Upline'}
            </button>
          </div>
        )}
      </div>

      {false && (
        <>
          {/* Downline Section — legacy static placeholder kept unreachable during API migration */}
      <div className='bg-white rounded-lg shadow-sm border border-gray-200'>
        <div className='px-4 md:px-6 py-4 border-b border-gray-200'>
          <h3 className='text-lg font-semibold text-gray-900'>Downline</h3>

          <div className='mt-4 border-b border-gray-200'>
            <nav
              className='flex -mb-px space-x-6'
              role='tablist'
              aria-label='Downline tabs'
            >
              <button
                type='button'
                className='border-blue-600 text-blue-600 whitespace-nowrap border-b-2 px-1 py-2 text-sm font-medium'
              >
                Shopper
              </button>
            </nav>
          </div>

          <p className='text-sm text-gray-500 mt-3'>
            Showing 1 to 20 of 100 downline members
          </p>
        </div>

        <div className='overflow-x-auto'>
          <table className='min-w-full divide-y divide-gray-200'>
            <thead className='bg-gray-50'>
              <tr>
                <th className='hidden lg:table-cell px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-32'>
                  ID
                </th>
                <th className='px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider'>
                  First Name
                </th>
                <th className='px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider'>
                  Last Name
                </th>
                <th className='px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider'>
                  Status
                </th>
                <th className='px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider'>
                  Join Date
                </th>
                <th className='px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider'>
                  Referral Code
                </th>
                <th className='px-3 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider'>
                  Direct Partner
                </th>
                <th className='px-3 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider'>
                  Downline
                </th>
                <th className='px-3 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider'>
                  Action
                </th>
              </tr>
            </thead>
            <tbody className='bg-white divide-y divide-gray-200'>
              {[
                {
                  id: 'a700a711-e57a-4577-b102-005849c6ac44',
                  firstName: 'Mark',
                  lastName: 'Brian',
                  status: 'Plus',
                  joinDate: '4/23/2026',
                  referral: 'RkN0b3hzb2h2eU5BNGVwYTpN',
                  partner: 1,
                  downline: 1,
                },
                {
                  id: 'f800b112-e47a-4577-b202-005849c6ac45',
                  firstName: 'Jane',
                  lastName: 'Smith',
                  status: 'Basic',
                  joinDate: '5/12/2027',
                  referral: 'RkN0b3hzb2h2eU5BNGVwYTpO',
                  partner: 2,
                  downline: 2,
                },
                {
                  id: 'd400c113-e67a-4577-b302-005849c6ac46',
                  firstName: 'Alice',
                  lastName: 'Johnson',
                  status: 'Plus',
                  joinDate: '6/30/2028',
                  referral: 'RkN0b3hzb2h2eU5BNGVwYTpP',
                  partner: 3,
                  downline: 3,
                },
                {
                  id: 'c500d114-e77a-4577-b402-005849c6ac47',
                  firstName: 'Bob',
                  lastName: 'Williams',
                  status: 'Plus',
                  joinDate: '7/15/2029',
                  referral: 'RkN0b3hzb2h2eU5BNGVwYTpQ',
                  partner: 4,
                  downline: 4,
                },
                {
                  id: 'e600e115-e88a-4577-b502-005849c6ac48',
                  firstName: 'Charlie',
                  lastName: 'Brown',
                  status: 'Plus',
                  joinDate: '8/25/2030',
                  referral: 'RkN0b3hzb2h2eU5BNGVwYTpR',
                  partner: 5,
                  downline: 5,
                },
                {
                  id: 'g700f116-e99a-4577-b602-005849c6ac49',
                  firstName: 'Diana',
                  lastName: 'Miller',
                  status: 'Basic',
                  joinDate: '9/10/2031',
                  referral: 'RkN0b3hzb2h2eU5BNGVwYTpS',
                  partner: 6,
                  downline: 6,
                },
                {
                  id: 'h800g117-e10a-4577-b702-005849c6ac50',
                  firstName: 'Edward',
                  lastName: 'Davis',
                  status: 'Plus',
                  joinDate: '10/5/2032',
                  referral: 'RkN0b3hzb2h2eU5BNGVwYTpT',
                  partner: 7,
                  downline: 7,
                },
                {
                  id: 'i900h118-e21a-4577-b802-005849c6ac51',
                  firstName: 'Fiona',
                  lastName: 'Garcia',
                  status: 'Plus',
                  joinDate: '11/20/2033',
                  referral: 'RkN0b3hzb2h2eU5BNGVwYTpU',
                  partner: 8,
                  downline: 8,
                },
                {
                  id: 'j000i119-e32a-4577-b902-005849c6ac52',
                  firstName: 'George',
                  lastName: 'Martinez',
                  status: 'Plus',
                  joinDate: '12/30/2034',
                  referral: 'RkN0b3hzb2h2eU5BNGVwYTpV',
                  partner: 9,
                  downline: 9,
                },
                {
                  id: 'k100j120-e43a-4577-ba02-005849c6ac53',
                  firstName: 'Hannah',
                  lastName: 'Lopez',
                  status: 'Plus',
                  joinDate: '1/15/2035',
                  referral: 'RkN0b3hzb2h2eU5BNGVwYTpW',
                  partner: 10,
                  downline: 10,
                },
                {
                  id: 'l200k121-e54a-4577-ba12-005849c6ac54',
                  firstName: 'Isaac',
                  lastName: 'Wilson',
                  status: 'Basic',
                  joinDate: '2/28/2036',
                  referral: 'RkN0b3hzb2h2eU5BNGVwYTpX',
                  partner: 11,
                  downline: 11,
                },
                {
                  id: 'm300l122-e65a-4577-ba22-005849c6ac55',
                  firstName: 'Julia',
                  lastName: 'Anderson',
                  status: 'Plus',
                  joinDate: '3/19/2037',
                  referral: 'RkN0b3hzb2h2eU5BNGVwYTpY',
                  partner: 12,
                  downline: 12,
                },
                {
                  id: 'n400m123-e76a-4577-ba32-005849c6ac56',
                  firstName: 'Kevin',
                  lastName: 'Thomas',
                  status: 'Plus',
                  joinDate: '4/10/2038',
                  referral: 'RkN0b3hzb2h2eU5BNGVwYTpZ',
                  partner: 13,
                  downline: 13,
                },
                {
                  id: 'o500n124-e87a-4577-ba42-005849c6ac57',
                  firstName: 'Laura',
                  lastName: 'Jackson',
                  status: 'Plus',
                  joinDate: '5/22/2039',
                  referral: 'RkN0b3hzb2h2eU5BNGVwYTp0',
                  partner: 14,
                  downline: 14,
                },
                {
                  id: 'p600o125-e98a-4577-ba52-005849c6ac58',
                  firstName: 'Michael',
                  lastName: 'White',
                  status: 'Plus',
                  joinDate: '6/14/2040',
                  referral: 'RkN0b3hzb2h2eU5BNGVwYTp1',
                  partner: 15,
                  downline: 15,
                },
                {
                  id: 'q700p126-e09a-4577-ba62-005849c6ac59',
                  firstName: 'Nina',
                  lastName: 'Harris',
                  status: 'Basic',
                  joinDate: '7/30/2041',
                  referral: 'RkN0b3hzb2h2eU5BNGVwYTp2',
                  partner: 16,
                  downline: 16,
                },
                {
                  id: 'r800q127-e10a-4577-ba72-005849c6ac60',
                  firstName: 'Oliver',
                  lastName: 'Clark',
                  status: 'Plus',
                  joinDate: '8/15/2042',
                  referral: 'RkN0b3hzb2h2eU5BNGVwYTp3',
                  partner: 17,
                  downline: 17,
                },
                {
                  id: 's900r128-e21a-4577-ba82-005849c6ac61',
                  firstName: 'Paula',
                  lastName: 'Robinson',
                  status: 'Plus',
                  joinDate: '9/26/2043',
                  referral: 'RkN0b3hzb2h2eU5BNGVwYTp4',
                  partner: 18,
                  downline: 18,
                },
                {
                  id: 't000s129-e32a-4577-ba92-005849c6ac62',
                  firstName: 'Quentin',
                  lastName: 'Hall',
                  status: 'Plus',
                  joinDate: '10/31/2044',
                  referral: 'RkN0b3hzb2h2eU5BNGVwYTp5',
                  partner: 19,
                  downline: 19,
                },
                {
                  id: 'u100t130-e43a-4577-bb02-005849c6ac63',
                  firstName: 'Rachel',
                  lastName: 'Young',
                  status: 'Plus',
                  joinDate: '11/21/2045',
                  referral: 'RkN0b3hzb2h2eU5BNGVwYTp6',
                  partner: 20,
                  downline: 20,
                },
                {
                  id: 'v200u131-e54a-4577-bb12-005849c6ac64',
                  firstName: 'Samuel',
                  lastName: 'King',
                  status: 'Basic',
                  joinDate: '12/15/2046',
                  referral: 'RkN0b3hzb2h2eU5BNGVwYTp7',
                  partner: 21,
                  downline: 21,
                },
              ].map((member, index) => {
                const isHighlight = member.status === 'Plus';
                return (
                  <tr key={index} className='hover:bg-gray-50 align-top'>
                    <td className='hidden lg:table-cell px-3 py-4 text-xs text-gray-900 font-mono align-top'>
                      <div
                        className='max-w-[160px] break-words'
                        title={member.id}
                      >
                        {member.id}
                      </div>
                    </td>
                    <td className='px-4 py-4 align-top'>
                      <div className='text-sm font-medium text-gray-900'>
                        {member.firstName}
                      </div>
                    </td>
                    <td className='px-3 py-4 text-sm font-medium text-gray-900 align-top'>
                      {member.lastName}
                    </td>
                    <td className='px-3 py-4 whitespace-nowrap align-top'>
                      <span
                        className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                          isHighlight
                            ? 'bg-blue-100 text-blue-800'
                            : 'bg-gray-100 text-gray-800'
                        }`}
                      >
                        {member.status}
                      </span>
                    </td>
                    <td className='px-3 py-4 whitespace-nowrap text-sm text-gray-900 align-top'>
                      {member.joinDate}
                    </td>
                    <td className='px-3 py-4 text-xs text-gray-900 font-mono align-top'>
                      <div
                        className='max-w-[180px] break-words'
                        title={member.referral}
                      >
                        {member.referral}
                      </div>
                    </td>
                    <td className='px-3 py-4 whitespace-nowrap text-sm text-gray-900 text-right font-semibold align-top'>
                      {member.partner}
                    </td>
                    <td className='px-3 py-4 whitespace-nowrap text-sm text-gray-900 text-right font-semibold align-top'>
                      {member.downline}
                    </td>
                    <td className='px-3 py-4 whitespace-nowrap text-center align-top'>
                      <button
                        type='button'
                        onClick={() => handleDownlineDetail(member)}
                        className='text-blue-600 hover:text-blue-900 text-sm font-medium'
                      >
                        Detail
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Static pagination — placeholder until API is wired */}
        <div className='px-6 py-4 border-t border-gray-200 flex items-center justify-between'>
          <p className='text-sm text-gray-700'>
            Page <span className='font-medium'>1</span> of{' '}
            <span className='font-medium'>50</span>
          </p>
          <nav
            className='relative z-0 inline-flex rounded-md shadow-sm -space-x-px'
            aria-label='Pagination'
          >
            <button
              type='button'
              className='relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50'
            >
              <svg className='h-5 w-5' fill='currentColor' viewBox='0 0 20 20'>
                <path
                  fillRule='evenodd'
                  d='M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z'
                  clipRule='evenodd'
                />
              </svg>
            </button>
            {[1, 2, 3].map(num => (
              <button
                key={num}
                type='button'
                className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                  num === 1
                    ? 'z-10 bg-blue-50 border-blue-500 text-blue-600'
                    : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                }`}
              >
                {num}
              </button>
            ))}
            <button
              type='button'
              className='relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50'
            >
              <svg className='h-5 w-5' fill='currentColor' viewBox='0 0 20 20'>
                <path
                  fillRule='evenodd'
                  d='M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z'
                  clipRule='evenodd'
                />
              </svg>
            </button>
          </nav>
        </div>
      </div>
        </>
      )}

      <RevealPasswordModal
        isOpen={revealModalOpen}
        onClose={() => setRevealModalOpen(false)}
        onSuccess={() => fetchMerchantDetails()}
      />
    </div>
  );
};

export default MerchantDetailPage;
